import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Edit, MapPin, Calendar, TrendingUp, Building2 } from 'lucide-react';

interface PromotionHeaderProps {
  promotion: any;
  onUpdate: () => void;
}

const PromotionHeader: React.FC<PromotionHeaderProps> = ({ promotion, onUpdate }) => {
  const available = promotion.available || 0;
  const reserved = promotion.reserved || 0;
  const sold = promotion.sold || 0;
  const totalUnits = available + reserved + sold;
  const totalBlocks = promotion.blocks?.length || 0;

  return (
    <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
          <Link to="/promotions" className="hover:text-blue-600 flex items-center space-x-1">
            <ArrowLeft className="w-4 h-4" />
            <span>Promotions</span>
          </Link>
          <span>/</span>
          <span className="text-gray-900 dark:text-white">{promotion.name}</span>
        </div>

        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {promotion.name}
              </h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                promotion.status === 'published' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                promotion.status === 'draft' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' :
                'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
              }`}>
                {promotion.status === 'published' ? 'Publiée' :
                 promotion.status === 'draft' ? 'Brouillon' : 'Archivée'}
              </span>
            </div>

            <div className="flex items-center space-x-6 text-gray-600 dark:text-gray-400">
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4" />
                <span>{promotion.city}, {promotion.wilaya}</span>
              </div>
              {promotion.delivery_date && (
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>
                    Livraison : {new Date(promotion.delivery_date).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              )}
              {promotion.construction_progress !== undefined && (
                <div className="flex items-center space-x-2">
                  <TrendingUp className="w-4 h-4" />
                  <span>{promotion.construction_progress}% d'avancement</span>
                </div>
              )}
            </div>
          </div>

          <Link
            to={`/promotions/${promotion.id}/edit`}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
          >
            <Edit className="w-4 h-4" />
            <span>Modifier</span>
          </Link>
        </div>

        <div className="grid grid-cols-5 gap-4">
          <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 dark:text-purple-400">Total blocs</p>
                <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                  {totalBlocks}
                </p>
              </div>
              <Building2 className="w-8 h-8 text-purple-400" />
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total lots</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {totalUnits}
                </p>
              </div>
              <Building2 className="w-8 h-8 text-gray-400" />
            </div>
          </div>

          <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 dark:text-green-400">Disponibles</p>
                <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                  {available}
                </p>
              </div>
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white font-bold text-sm">
                {totalUnits > 0
                  ? Math.round((available / totalUnits) * 100)
                  : 0}%
              </div>
            </div>
          </div>

          <div className="bg-orange-50 dark:bg-orange-900/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 dark:text-orange-400">Réservés</p>
                <p className="text-2xl font-bold text-orange-900 dark:text-orange-100">
                  {reserved}
                </p>
              </div>
              <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-sm">
                {totalUnits > 0
                  ? Math.round((reserved / totalUnits) * 100)
                  : 0}%
              </div>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 dark:text-blue-400">Vendus</p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {sold}
                </p>
              </div>
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-sm">
                {totalUnits > 0
                  ? Math.round((sold / totalUnits) * 100)
                  : 0}%
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromotionHeader;
